export const BASE_HEADERS = ['StoreId', 'Action', 'Description'];
export const STANDARD_POINTS_HEADER = 'Points';
export const SEGREGATED_POINTS_HEADERS = [
  'Loyalty Points',
  'Engagement Points'
];
export const VALID_ACTIONS = ['increase', 'decrease', 'equal', 'equaldays'];

export const POINTS_FORMAT = Object.freeze({
  STANDARD: 'standard',
  SEGREGATED: 'segregated'
});

const INTEGER_REGEX = /^-?\d+$/;
const ALPHANUMERIC_HYPHEN_REGEX = /^[a-z0-9\-/]+$/i;
const STORE_ID_MIN_LENGTH = 1;
const STORE_ID_MAX_LENGTH = 255;

const INVALID_POINTS_FORMAT_MESSAGE =
  "Invalid points format: use either 'Points' or both 'Loyalty Points' and 'Engagement Points'";

export const validateFile = (file) => {
  const errors = [];

  if (!file || file.size === 0) {
    errors.push('Uploaded file is empty');
  }

  if (!file?.name?.toLowerCase().endsWith('.csv')) {
    errors.push('Invalid file format. Only CSV files are allowed.');
  }

  return {isValid: errors.length === 0, errors};
};

export const detectDelimiter = (fileText) => {
  const firstLine =
    fileText
      ?.split(/\r?\n/)
      .map((line) => line.trim())
      .find((line) => line.length > 0) || '';

  const commaCount = (firstLine.match(/,/g) || []).length;
  const semicolonCount = (firstLine.match(/;/g) || []).length;

  return semicolonCount > commaCount ? ';' : ',';
};

const normalizeHeaders = (headers) =>
  headers
    .map((header) => String(header).trim())
    .filter((header) => header !== '');

const detectPointsFormat = (headers) => {
  const hasPoints = headers.includes(STANDARD_POINTS_HEADER);
  const hasLoyalty = headers.includes(SEGREGATED_POINTS_HEADERS[0]);
  const hasEngagement = headers.includes(SEGREGATED_POINTS_HEADERS[1]);

  if (hasPoints && (hasLoyalty || hasEngagement)) {
    return {pointsFormat: null, error: INVALID_POINTS_FORMAT_MESSAGE};
  }

  if (hasPoints) {
    return {pointsFormat: POINTS_FORMAT.STANDARD, error: null};
  }

  if (hasLoyalty && hasEngagement) {
    return {pointsFormat: POINTS_FORMAT.SEGREGATED, error: null};
  }

  if (hasLoyalty || hasEngagement) {
    return {pointsFormat: null, error: INVALID_POINTS_FORMAT_MESSAGE};
  }

  return {pointsFormat: null, error: INVALID_POINTS_FORMAT_MESSAGE};
};

const getAllowedHeaders = (pointsFormat) => {
  if (pointsFormat === POINTS_FORMAT.STANDARD) {
    return [...BASE_HEADERS, STANDARD_POINTS_HEADER];
  }

  if (pointsFormat === POINTS_FORMAT.SEGREGATED) {
    return [...BASE_HEADERS, ...SEGREGATED_POINTS_HEADERS];
  }

  return [...BASE_HEADERS];
};

export const validateHeaders = (headers) => {
  const errors = [];
  const normalizedHeaders = normalizeHeaders(headers);

  const missingBaseHeaders = BASE_HEADERS.filter(
    (header) => !normalizedHeaders.includes(header)
  );

  if (missingBaseHeaders.length > 0) {
    errors.push(`Missing required column(s): ${missingBaseHeaders.join(', ')}`);
  }

  const {pointsFormat, error: pointsFormatError} =
    detectPointsFormat(normalizedHeaders);

  if (pointsFormatError) {
    errors.push(pointsFormatError);
  }

  const allowedHeaders = getAllowedHeaders(pointsFormat);
  const unexpectedHeaders = normalizedHeaders.filter(
    (header) => !allowedHeaders.includes(header)
  );

  if (unexpectedHeaders.length > 0) {
    errors.push(`Unexpected column(s) found: ${unexpectedHeaders.join(', ')}`);
  }

  return {
    isValid: errors.length === 0,
    errors,
    pointsFormat,
    allowedHeaders
  };
};

const isValidInteger = (value) => INTEGER_REGEX.test(value);

const validateStandardPoints = (row, rowNumber, errors) => {
  const points = row[STANDARD_POINTS_HEADER]?.trim();

  if (!points) {
    errors.push(`Row ${rowNumber}: '${STANDARD_POINTS_HEADER}' is required.`);
    return;
  }

  if (!isValidInteger(points)) {
    errors.push(
      `Row ${rowNumber}: '${STANDARD_POINTS_HEADER}' must be a valid integer.`
    );
  }
};

const validateSegregatedPoints = (row, rowNumber, errors) => {
  const loyaltyPoints = row[SEGREGATED_POINTS_HEADERS[0]]?.trim() || '';
  const engagementPoints = row[SEGREGATED_POINTS_HEADERS[1]]?.trim() || '';

  if (!loyaltyPoints && !engagementPoints) {
    errors.push(
      `Row ${rowNumber}: At least one of 'Loyalty Points' or 'Engagement Points' is required.`
    );
    return;
  }

  if (loyaltyPoints && !isValidInteger(loyaltyPoints)) {
    errors.push(`Row ${rowNumber}: 'Loyalty Points' must be a valid integer.`);
  }

  if (engagementPoints && !isValidInteger(engagementPoints)) {
    errors.push(
      `Row ${rowNumber}: 'Engagement Points' must be a valid integer.`
    );
  }
};

export const validateRow = (row, rowIndex, {pointsFormat, allowedHeaders}) => {
  const errors = [];
  const rowNumber = rowIndex + 2;

  Object.keys(row).forEach((key) => {
    const trimmedKey = String(key).trim();
    if (
      trimmedKey &&
      !trimmedKey.startsWith('_') &&
      allowedHeaders &&
      !allowedHeaders.includes(trimmedKey)
    ) {
      errors.push(
        `Row ${rowNumber}: Unexpected value in column '${trimmedKey}'.`
      );
    }
  });

  const storeId = row.StoreId?.trim();
  if (!storeId) {
    errors.push(`Row ${rowNumber}: 'StoreId' is required.`);
  } else {
    if (!ALPHANUMERIC_HYPHEN_REGEX.test(storeId)) {
      errors.push(
        `Row ${rowNumber}: 'StoreId' should be alphanumeric and may include hyphens or slashes.`
      );
    } else if (
      storeId.length < STORE_ID_MIN_LENGTH ||
      storeId.length > STORE_ID_MAX_LENGTH
    ) {
      errors.push(
        `Row ${rowNumber}: 'StoreId' should be between ${STORE_ID_MIN_LENGTH} and ${STORE_ID_MAX_LENGTH} characters.`
      );
    }
  }

  const action = row.Action?.trim();
  if (!action) {
    errors.push(`Row ${rowNumber}: 'Action' is required.`);
  } else if (!VALID_ACTIONS.includes(action.toLowerCase())) {
    errors.push(
      `Row ${rowNumber}: 'Action' must be one of: ${VALID_ACTIONS.join(', ')}.`
    );
  }

  if (pointsFormat === POINTS_FORMAT.STANDARD) {
    validateStandardPoints(row, rowNumber, errors);
  } else if (pointsFormat === POINTS_FORMAT.SEGREGATED) {
    validateSegregatedPoints(row, rowNumber, errors);
  }

  return errors;
};

const cleanCSVData = (csvData) =>
  csvData
    ?.filter((row) =>
      Object.values(row).some(
        (val) => val !== undefined && val !== null && String(val).trim() !== ''
      )
    )
    .map((row) => {
      const cleanRow = {};
      Object.entries(row).forEach(([key, val]) => {
        const trimmedKey = String(key).trim();
        cleanRow[trimmedKey] =
          val !== undefined && val !== null ? String(val).trim() : '';
      });
      return cleanRow;
    }) || [];

export const validateBulkPointModifierCsv = (file, parsedRows, headers) => {
  const errors = [];

  const fileValidation = validateFile(file);
  errors.push(...fileValidation.errors);

  const cleanedData = cleanCSVData(parsedRows);

  if (cleanedData.length === 0) {
    errors.push('CSV file contains no valid data');
    return {isInvalidFileData: true, errorsList: errors};
  }

  const headerValidation = validateHeaders(
    headers || Object.keys(cleanedData[0])
  );
  errors.push(...headerValidation.errors);

  if (headerValidation.isValid) {
    cleanedData.forEach((row, rowIndex) => {
      errors.push(
        ...validateRow(row, rowIndex, {
          pointsFormat: headerValidation.pointsFormat,
          allowedHeaders: headerValidation.allowedHeaders
        })
      );
    });
  }

  return {
    isInvalidFileData: errors.length > 0,
    errorsList: errors
  };
};
